/**
 * Adding people to a peer-to-peer 1:1 call moves it into a new group call
 * that names the 1:1 it continues. The person on the line must join that
 * group without ringing, and the 1:1 must end without "Call ended".
 *
 * The 1:1 lives in CallContext and the group in GroupCallContext, so this
 * registry sits between them: which 1:1 calls this tab is on (or left a
 * moment ago, since the group's invite and the 1:1's end can arrive in
 * either order), and how to end one quietly once the group takes over.
 */

/** Longer than the gap between the 1:1 ending and the group's invite. */
export const CONTINUED_CALL_WINDOW_MS = 60_000;

const onCall = new Set<string>();
const leftAt = new Map<string, number>();
let releaseDirectCall: ((callId: string) => void) | null = null;

/** This tab is on (answered or placed and connected) this 1:1 call. */
export function noteOnDirectCall(callId: string) {
  if (!callId) return;
  onCall.add(callId);
  leftAt.delete(callId);
}

/** This tab left the 1:1 call; it still counts for a short while. */
export function noteOffDirectCall(callId: string, now = Date.now()) {
  if (!onCall.delete(callId)) return;
  leftAt.set(callId, now);
}

/** Whether a group call continuing `callId` carries this tab's own call. */
export function wasOnDirectCall(callId: string | null | undefined, now = Date.now()) {
  if (!callId) return false;
  if (onCall.has(callId)) return true;
  const left = leftAt.get(callId);
  if (left == null) return false;
  if (now - left <= CONTINUED_CALL_WINDOW_MS) return true;
  leftAt.delete(callId);
  return false;
}

/** CallContext ends a 1:1 call quietly through this. */
export function bindDirectCallRelease(release: ((callId: string) => void) | null) {
  releaseDirectCall = release;
}

/** The group took over: end the 1:1 here without "Call ended" or a hang-up. */
export function releaseContinuedCall(callId: string) {
  releaseDirectCall?.(callId);
}

export function resetContinuedCallsForTests() {
  onCall.clear();
  leftAt.clear();
  releaseDirectCall = null;
}
